import { ENDPOINT_EXAM_CONTEXT } from "@constants/urls";
import authAxios from "@helpers/lib/axios";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import type { Choice } from "@memorang/types";
import type { ProductType } from "@memorang/types";
import type { ExamDetails } from "@memorang/types";
import type { CurrentExamContext } from "../contexts/types";

const examToProductIdsMapping: Record<
	string,
	{
		studyPackProductId: string;
		studyPackProductNumericId: number;
		practiceTestProductId: string;
		practiceTestProductNumericId: number;
	}
> = {
	"81839ecf-5d22-488c-8d0d-3b45aa967df1": {
		studyPackProductId: "124b7081-6cdf-46bd-b22e-9656911376cc",
		studyPackProductNumericId: 253,
		practiceTestProductId: "03225547-0191-46dc-87c9-ddc5eb739230",
		practiceTestProductNumericId: 254,
	},
};
export const getProductTitle = (bundleType: ProductType) => {
	switch (bundleType) {
		case "STUDY_PACK":
			return "Study-Pack";
		case "PRACTICE_TEST":
			return "Practice Tests";
		default:
			return "";
	}
};
const constructClientExamContext = ({
	exam: { name, bundles, icon, studyPackCollection, id },
	metadata: { examDate },
}: ExamDetails) => {
	const studyPack = bundles.find(
		(item) => item.bundle.bundleType === "STUDY_PACK",
	)?.bundle;
	const practiceTest = bundles.find(
		(item) => item.bundle.bundleType === "PRACTICE_TEST",
	)?.bundle;

	return {
		examName: name,
		examId: id,
		studyPackProductNumericId:
			studyPack?.numericId! ||
			examToProductIdsMapping[id]?.studyPackProductNumericId,
		studyPackProductId:
			studyPack?.id! || examToProductIdsMapping[id]?.studyPackProductId,
		practiceTestProductNumericId:
			practiceTest?.numericId ||
			examToProductIdsMapping[id]?.practiceTestProductNumericId,
		practiceTestProductId:
			practiceTest?.id! || examToProductIdsMapping[id]?.practiceTestProductId,
		examDate: examDate,
		examIcon: icon,
		studyPackCollectionId: studyPackCollection[0],
	};
};

const getNumPercenetageAnswered = (
	items: Choice[],
	currentChoiceNumFirstSelections: number,
	answered?: boolean,
) => {
	const isSomeItemWithMoreSelections = items.some(
		({ numFirstSelections }) => numFirstSelections && numFirstSelections > 10,
	);
	const showPercentAnswered = isSomeItemWithMoreSelections && answered;

	const totalfirstSelections = items.reduce(
		(a, b) => a + (b.numFirstSelections || 0),
		0,
	);

	const average = totalfirstSelections
		? (currentChoiceNumFirstSelections || 0) / totalfirstSelections
		: 0;

	const percentAnswered =
		totalfirstSelections && showPercentAnswered
			? Number.parseFloat((average * 100).toFixed(0))
			: undefined;
	return percentAnswered;
};

const storeExamContextInKV = async (examContext: CurrentExamContext) => {
	try {
		await authAxios.post(ENDPOINT_EXAM_CONTEXT, { examContext });
	} catch (error) {
		const errorObj = new Error("Error storing exam context in KV");
		captureErrorToSentry(errorObj, {
			error: serializeErrorForSentry(error),
		});
	}
};

const retrieveExamContextFromKV = async () => {
	try {
		const response = await authAxios.get(ENDPOINT_EXAM_CONTEXT, {
			validateStatus: () => true,
		});
		if (response.status === 200) {
			return response.data as CurrentExamContext;
		}
		return null;
	} catch (error) {
		console.error("Error:", error);
	}
};

export {
	constructClientExamContext,
	getNumPercenetageAnswered,
	storeExamContextInKV,
	retrieveExamContextFromKV,
};
